import { useMemo, useState } from "react";
import { Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { Skeleton } from "@/components/skeleton";
import {
  createCustomField,
  deleteCustomField,
  updateCustomField,
  useCustomFields,
  type CustomFieldDefinition,
  type CustomFieldEntityType,
  type CustomFieldOption,
  type CustomFieldType,
} from "@/hooks/use-custom-fields";

const ENTITY_OPTIONS: CustomFieldEntityType[] = ["person", "company", "deal"];
const FIELD_TYPE_OPTIONS: CustomFieldType[] = ["text", "number", "date", "boolean", "select"];

interface CustomFieldDraft {
  fieldLabel: string;
  fieldKey: string;
  fieldType: CustomFieldType;
  required: boolean;
  showInList: boolean;
  showInCard: boolean;
  options: CustomFieldOption[];
}

const EMPTY_OPTION = (): CustomFieldOption => ({ value: "", label: "", color: "" });
const DEFAULT_DRAFT = (): CustomFieldDraft => ({
  fieldLabel: "",
  fieldKey: "",
  fieldType: "text",
  required: false,
  showInList: true,
  showInCard: true,
  options: [EMPTY_OPTION()],
});

function normalizeOptions(options: CustomFieldOption[], fieldType: CustomFieldType) {
  if (fieldType !== "select") {
    return undefined;
  }

  return options
    .map((option) => ({
      value: option.value.trim(),
      label: option.label.trim(),
      color: option.color?.trim() || undefined,
    }))
    .filter((option) => option.value.length > 0 && option.label.length > 0);
}

function buildDraftFromField(field: CustomFieldDefinition): CustomFieldDraft {
  return {
    fieldLabel: field.fieldLabel,
    fieldKey: field.fieldKey,
    fieldType: field.fieldType,
    required: field.required,
    showInList: field.showInList,
    showInCard: field.showInCard,
    options: field.options && field.options.length > 0
      ? field.options.map((option) => ({ ...option, color: option.color ?? "" }))
      : [EMPTY_OPTION()],
  };
}

export default function CustomFieldsSettingsPage() {
  const [entityType, setEntityType] = useState<CustomFieldEntityType>("person");
  const { fields, isLoading, error, setFields } = useCustomFields(entityType);
  const [surfaceError, setSurfaceError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CustomFieldDraft>(DEFAULT_DRAFT);

  const canSave = useMemo(() => {
    if (draft.fieldLabel.trim().length === 0 || draft.fieldKey.trim().length === 0) {
      return false;
    }

    if (draft.fieldType !== "select") {
      return true;
    }

    return (normalizeOptions(draft.options, draft.fieldType) ?? []).length > 0;
  }, [draft]);

  const resetDraft = () => {
    setEditingFieldId(null);
    setDraft(DEFAULT_DRAFT());
  };

  const handleSave = async () => {
    if (!canSave) return;

    setIsSaving(true);
    setSurfaceError(null);
    try {
      const payload = {
        entityType,
        fieldLabel: draft.fieldLabel.trim(),
        fieldKey: draft.fieldKey.trim(),
        fieldType: draft.fieldType,
        options: normalizeOptions(draft.options, draft.fieldType),
        required: draft.required,
        showInList: draft.showInList,
        showInCard: draft.showInCard,
        displayOrder: editingFieldId ? undefined : fields.length,
      };

      const saved = editingFieldId
        ? await updateCustomField(editingFieldId, payload)
        : await createCustomField(payload);

      setFields((current) => {
        if (!editingFieldId) {
          return [...current, saved];
        }
        return current.map((field) => (field.id === editingFieldId ? saved : field));
      });
      resetDraft();
    } catch (caughtError) {
      setSurfaceError(caughtError instanceof Error ? caughtError.message : "Failed to save custom field");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setSurfaceError(null);
    try {
      await deleteCustomField(id);
      setFields((current) => current.filter((field) => field.id !== id));
      if (editingFieldId === id) {
        resetDraft();
      }
    } catch (caughtError) {
      setSurfaceError(caughtError instanceof Error ? caughtError.message : "Failed to delete custom field");
    }
  };

  const handleEdit = (field: CustomFieldDefinition) => {
    setEditingFieldId(field.id);
    setDraft(buildDraftFromField(field));
    setSurfaceError(null);
  };

  const updateDraftOption = (index: number, patch: Partial<CustomFieldOption>) => {
    setDraft((current) => ({
      ...current,
      options: current.options.map((option, optionIndex) => (
        optionIndex === index ? { ...option, ...patch } : option
      )),
    }));
  };

  const addOption = () => {
    setDraft((current) => ({
      ...current,
      options: [...current.options, EMPTY_OPTION()],
    }));
  };

  const removeOption = (index: number) => {
    setDraft((current) => ({
      ...current,
      options: current.options.length > 1
        ? current.options.filter((_, optionIndex) => optionIndex !== index)
        : [EMPTY_OPTION()],
    }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Custom fields</h1>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          Manage mounted CRM field definitions for contacts, companies, and deals.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {ENTITY_OPTIONS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => {
              setEntityType(option);
              resetDraft();
            }}
            className={`stagger-item rounded-full px-3 py-1.5 text-sm capitalize transition-colors ${
              entityType === option
                ? "bg-[var(--color-primary)] text-white"
                : "bg-[var(--color-surface)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      {(error || surfaceError) ? (
        <div className="rounded-xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error ?? surfaceError}
        </div>
      ) : null}

      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">
            {editingFieldId ? "Edit field" : "Create field"}
          </h2>
          {editingFieldId ? (
            <button
              type="button"
              onClick={resetDraft}
              className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-muted-foreground)]"
            >
              <X className="h-4 w-4" />
              Cancel edit
            </button>
          ) : null}
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="space-y-2 text-sm text-[var(--color-muted-foreground)]">
            <span>Field label</span>
            <input
              value={draft.fieldLabel}
              onChange={(event) => setDraft((current) => ({ ...current, fieldLabel: event.target.value }))}
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
            />
          </label>
          <label className="space-y-2 text-sm text-[var(--color-muted-foreground)]">
            <span>Field key</span>
            <input
              value={draft.fieldKey}
              onChange={(event) => setDraft((current) => ({ ...current, fieldKey: event.target.value }))}
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
            />
          </label>
          <label className="space-y-2 text-sm text-[var(--color-muted-foreground)]">
            <span>Field type</span>
            <select
              value={draft.fieldType}
              onChange={(event) => setDraft((current) => ({ ...current, fieldType: event.target.value as CustomFieldType }))}
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
            >
              {FIELD_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <div className="grid gap-2 self-end text-sm text-[var(--color-muted-foreground)]">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.required}
                onChange={(event) => setDraft((current) => ({ ...current, required: event.target.checked }))}
              />
              Required
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.showInList}
                onChange={(event) => setDraft((current) => ({ ...current, showInList: event.target.checked }))}
              />
              Show in list
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.showInCard}
                onChange={(event) => setDraft((current) => ({ ...current, showInCard: event.target.checked }))}
              />
              Show in card
            </label>
          </div>
        </div>

        {draft.fieldType === "select" ? (
          <div className="mt-4 space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-[var(--color-foreground)]">Select options</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  Each option needs a label and value before it can be saved.
                </p>
              </div>
              <button
                type="button"
                onClick={addOption}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-muted-foreground)]"
              >
                <Plus className="h-4 w-4" />
                Add option
              </button>
            </div>

            <div className="space-y-3">
              {draft.options.map((option, index) => (
                <div key={`${index}-${option.label}-${option.value}`} className="grid gap-3 md:grid-cols-[minmax(0,1fr),minmax(0,1fr),10rem,auto]">
                  <input
                    aria-label={`Option label ${index + 1}`}
                    value={option.label}
                    onChange={(event) => updateDraftOption(index, { label: event.target.value })}
                    placeholder="Option label"
                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                  <input
                    aria-label={`Option value ${index + 1}`}
                    value={option.value}
                    onChange={(event) => updateDraftOption(index, { value: event.target.value })}
                    placeholder="option_value"
                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                  <input
                    aria-label={`Option color ${index + 1}`}
                    value={option.color ?? ""}
                    onChange={(event) => updateDraftOption(index, { color: event.target.value })}
                    placeholder="#0EA5E9"
                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                  <button
                    type="button"
                    aria-label={`Remove option ${index + 1}`}
                    onClick={() => removeOption(index)}
                    className="inline-flex items-center justify-center rounded-xl border border-[var(--color-border)] px-3 py-2 text-[var(--color-destructive)]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => { void handleSave(); }}
          disabled={!canSave || isSaving}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          {editingFieldId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {isSaving ? "Saving..." : editingFieldId ? "Save field" : `Add ${entityType} field`}
        </button>
      </section>

      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{entityType} fields</h2>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          Live definitions from the mounted custom-field routes.
        </p>

        <div className="mt-4 space-y-3">
          {isLoading ? (
            <>
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </>
          ) : fields.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">No custom fields created for {entityType} yet.</p>
          ) : fields.map((field) => (
            <div key={field.id} className="stagger-item flex items-center justify-between gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
              <div>
                <p className="font-medium text-[var(--color-foreground)]">{field.fieldLabel}</p>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  {field.fieldKey} · {field.fieldType} · order {field.displayOrder}
                </p>
                {field.options && field.options.length > 0 ? (
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    Options: {field.options.map((option) => option.label).join(", ")}
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[var(--color-muted-foreground)]">
                  {field.required ? "Required" : "Optional"} · {field.showInList ? "List" : "Hidden"} · {field.showInCard ? "Card" : "No card"}
                </span>
                <button
                  type="button"
                  aria-label={`Edit ${field.fieldLabel}`}
                  onClick={() => handleEdit(field)}
                  className="rounded-lg border border-[var(--color-border)] p-2 text-[var(--color-muted-foreground)]"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${field.fieldLabel}`}
                  onClick={() => { void handleDelete(field.id); }}
                  className="rounded-lg border border-[var(--color-border)] p-2 text-[var(--color-destructive)]"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
